import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { VenueFinancesService } from 'app/modules/sho_management_pro/venue/services/venue-finances.service';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AdapterService } from '../../services/adapters/adapter.service';
import { TerminalSalesService } from '../../services/sales/box-office/terminal-sales.service';
import * as TerminalSalesActions from './terminal-sales.actions';

@Injectable()
export class TerminalSalesEffects {
    private _actions$ = inject(Actions);
    private _terminalSalesService = inject(TerminalSalesService);
    private adapter = inject(AdapterService);
    private _venueFinancesService = inject(VenueFinancesService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all terminal sales',
        getOneError: 'Error getting a terminal sale',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All TerminalSales
    getAllTerminalSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(TerminalSalesActions.loadTerminalSales),
            switchMap((action) => {
                return this._terminalSalesService
                    .getAll(action.playdateId)
                    .pipe(
                        map((res) => {
                            const data = this.adapter.adaptTerminalSalesList(
                                res.data
                            );

                            return TerminalSalesActions.loadTerminalSalesSuccess(
                                {
                                    terminalSales: data,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                TerminalSalesActions.loadTerminalSalesFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get One TerminalSale
    // getTerminalSale$ = createEffect(() =>
    //     this._actions$.pipe(
    //         ofType(TerminalSalesActions.loadTerminalSale),
    //         switchMap((action) => {
    //             return this._terminalSalesService.getOne(action.id).pipe(
    //                 map((res) => {
    //                     return TerminalSalesActions.loadTerminalSaleSuccess({
    //                         terminalSale: res.data,
    //                     });
    //                 }),
    //                 catchError((error) =>
    //                     of(
    //                         TerminalSalesActions.loadTerminalSaleFailure({
    //                             error:
    //                                 error?.error?.message ||
    //                                 this.messages.unknownError,
    //                         })
    //                     )
    //                 )
    //             );
    //         })
    //     )
    // );

    // Create TerminalSale
    createTerminalSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(TerminalSalesActions.createTerminalSale),
            switchMap((action) => {
                return this._terminalSalesService.create(action.data).pipe(
                    map((res) => {
                        const data = res.data;
                        const sale = this.adapter.adaptTerminalSale(data);

                        if (!sale) {
                            throw new Error(this.messages.getOneError);
                        }

                        return TerminalSalesActions.createTerminalSaleSuccess({
                            terminalSale: sale,
                            success: 'Terminal sale created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            TerminalSalesActions.createTerminalSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Terminal Sale
    updateTerminalSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(TerminalSalesActions.updateTerminalSale),
            switchMap((action) => {
                return this._terminalSalesService.update(action.data).pipe(
                    // tap((res) => console.log(res)), // Debug
                    map((res) => {
                        const data = res.data;
                        const sale = this.adapter.adaptTerminalSale(data);

                        if (!sale) {
                            throw new Error(this.messages.getOneError);
                        }

                        return TerminalSalesActions.updateTerminalSaleSuccess({
                            terminalSale: sale,
                            success: 'Terminal sale updated successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            TerminalSalesActions.updateTerminalSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Terminal Sale
    deleteTerminalSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(TerminalSalesActions.deleteTerminalSale),
            switchMap((action) => {
                return this._terminalSalesService.delete(action.payload).pipe(
                    // tap((res) => console.log(res)), // Debug
                    map((res) => {
                        const deletedSales = res.data.deleted_sales;
                        return TerminalSalesActions.deleteTerminalSaleSuccess({
                            deletedSales: deletedSales,
                            success: 'Terminal sale deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            TerminalSalesActions.deleteTerminalSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Terminal Sale Item
    deleteTerminalSaleItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(TerminalSalesActions.deleteTerminalSaleItem),
            switchMap((action) => {
                return this._terminalSalesService.delete(action.payload).pipe(
                    // tap((res) => console.log(res)), // Debug
                    map((res) => {
                        const saleId = res.data.deleted_sales[0];

                        if (!saleId) {
                            const message =
                                'Sale item ID not found in response';
                            this._alertService.showAlert(message, 'error');
                            throw new Error(message);
                        }

                        return TerminalSalesActions.deleteTerminalSaleItemSuccess(
                            {
                                deletedSaleItem: saleId,
                                success:
                                    'Terminal sale item deleted successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            TerminalSalesActions.deleteTerminalSaleItemFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onTerminalSaleActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    TerminalSalesActions.createTerminalSaleSuccess,
                    TerminalSalesActions.updateTerminalSaleSuccess,
                    TerminalSalesActions.deleteTerminalSaleSuccess,
                    TerminalSalesActions.deleteTerminalSaleItemSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');

                    // Reload totals
                    this._venueFinancesService.onUpdateFinances = true;
                })
            ),
        { dispatch: false }
    );

    onTerminalSaleActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    TerminalSalesActions.loadTerminalSalesFailure,
                    TerminalSalesActions.loadTerminalSaleFailure,
                    TerminalSalesActions.createTerminalSaleFailure,
                    TerminalSalesActions.updateTerminalSaleFailure,
                    TerminalSalesActions.deleteTerminalSaleFailure,
                    TerminalSalesActions.deleteTerminalSaleItemFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
